/**
 * Synchronisation Hybride : pastille d'état (barre supérieure), panneau « Synchronisation » (fenêtre modale),
 * liste « À examiner » et écran de consentement.
 *
 * Composants EXISTANTS uniquement (`<nexxus-modal>`, `.btn`, `.info-banner`, `table.data`, `.nx-update-bar`,
 * `confirmAction`, `ToastService`, `trapFocus`) et variables du thème. La logique (moteur, file, licence) est
 * dans `HybridSyncRuntime` : ce fichier ne fait que l'afficher. Aucune insertion de HTML brut : tout le DOM est
 * construit par `h()` (voir HybridSyncDom.ts) et les textes passent par `textContent`.
 *
 * La synchronisation ne bloque jamais le travail : aucune boîte de dialogue bloquante du navigateur,
 * seulement des toasts discrets et ce panneau consultable.
 */
import type { HybridSyncRuntime, HybridSyncUiState } from '@application/sync/HybridSyncRuntime';
import { attentionText } from '@application/sync/HybridSyncLabels';
import type { HybridSyncInput } from '@application/sync/HybridSyncEligibility';
import type { SyncAttentionItem } from '@infra/sync/hybrid/SyncBridgeTypes';
import '@presentation/components/Modal';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { ToastService } from '@presentation/components/Toast';
import { downloadBlob } from '@presentation/shell/download';
import { CONSENT_CHECKBOX_TEXT, CONSENT_PARAGRAPHS } from '@presentation/shell/HybridSyncConsent';
import { h, replaceRegion } from '@presentation/shell/HybridSyncDom';
import { clearInitialSummary, getLastInitialSummary, subscribeInitialSummary } from '@presentation/shell/HybridSyncNotices';
import {
  TONE_VAR,
  buildAttentionSection,
  buildConsentContent,
  buildCountersBlock,
  buildProgressBar,
  buildStateBlock,
  buildSummaryBlock,
  pillText,
  showsProgress,
  toneDot,
  type AttentionLoad,
} from '@presentation/shell/HybridSyncPanel';
import { TEXT, itemTitle } from '@presentation/shell/HybridSyncText';
import { trapFocus } from '@shared/a11y/trapFocus';

const PANEL_ID = 'hybrid-sync-panel';

export { CONSENT_PARAGRAPHS, CONSENT_CHECKBOX_TEXT };

/** Ce que l'interface utilise du moteur d'exécution (le vrai `HybridSyncRuntime` convient tel quel). */
export type HybridSyncRuntimePort = Pick<
  HybridSyncRuntime,
  | 'getUiState'
  | 'subscribe'
  | 'activate'
  | 'listAttention'
  | 'resolveAttention'
  | 'syncNow'
  | 'acceptConsent'
  | 'withdrawConsent'
  | 'exportDiagnostic'
>;

export interface HybridSyncStatusOpts {
  root: HTMLElement;
  runtime: HybridSyncRuntimePort;
  /** Lit l'état courant (licence, session, société) pour réévaluer l'éligibilité. */
  getInput: () => Promise<HybridSyncInput>;
  /** Se reconnecter (par défaut : fermeture de session de l'application, après confirmation). */
  onReconnect?: () => void;
  /** Ouvrir la mise à jour de l'application (par défaut : mise à jour proposée par l'application). */
  onUpdate?: () => void;
}

type ModalEl = HTMLElement & { open: () => void; close: () => void };
type PanelMode = 'consent' | 'status';

function windowFunction(name: string): (() => unknown) | null {
  const candidate = (window as unknown as Record<string, unknown>)[name];
  return typeof candidate === 'function' ? (candidate as () => unknown) : null;
}

/** Monte l'indicateur et le panneau ; retourne la fonction de démontage. */
export function mountHybridSyncStatus(opts: HybridSyncStatusOpts): () => void {
  const { root, runtime, getInput } = opts;
  const host = root.querySelector<HTMLElement>('#hybrid-sync-indicator');
  if (!host) return () => undefined;

  let state: HybridSyncUiState = runtime.getUiState();
  let items: SyncAttentionItem[] = [];
  let attentionLoad: AttentionLoad = 'loading';
  let busyId: string | null = null;
  let syncBusy = false;
  let isAdmin = false;
  let lastTenant: string | null = state.tenantId;

  let modal: ModalEl | null = null;
  /** Unique conteneur du contenu : le piège de focus s'y applique (la croix de la fenêtre reste hors piège, « Fermer » fait pareil). */
  let body: HTMLElement | null = null;
  let mode: PanelMode | null = null;
  let releaseTrap: (() => void) | null = null;
  let closing = false;
  let regions: {
    state: HTMLElement;
    summary: HTMLElement;
    counters: HTMLElement;
    actions: HTMLElement;
    attention: HTMLElement;
  } | null = null;
  let syncButton: HTMLButtonElement | null = null;
  const signatures = new Map<string, string>();

  host.replaceChildren();
  host.hidden = true;

  // --- pastille ------------------------------------------------------------------------------------------

  const pillDot = h('span', { class: 'hsync-dot' });
  const live = h('span', { attrs: { 'aria-live': 'polite', 'aria-atomic': 'true' } });
  const pillBar = h('span', { hidden: true });
  const pill = h(
    'button',
    {
      type: 'button',
      class: 'btn btn-sm btn-ghost',
      id: 'hybrid-sync-pill',
      style: 'margin-right:8px;font-size:12px;font-weight:600;color:var(--marine);border-width:2px;',
      attrs: { 'aria-haspopup': 'dialog', 'aria-expanded': 'false' },
    },
    pillDot,
    live,
    pillBar,
  );
  const attention = h(
    'button',
    {
      type: 'button',
      class: 'btn btn-sm btn-ghost',
      id: 'hybrid-sync-attention',
      style: `margin-right:8px;font-size:12px;font-weight:600;color:var(--marine);border-width:2px;border-color:${TONE_VAR.warn};display:none;`,
      hidden: true,
      attrs: { 'aria-haspopup': 'dialog', 'aria-expanded': 'false' },
    },
    toneDot('warn'),
    h('span', { id: 'hybrid-sync-attention-text' }),
  );
  const attentionText$ = attention.querySelector<HTMLElement>('#hybrid-sync-attention-text') as HTMLElement;
  host.append(pill, attention);

  const setPillTone = (tone: 'ok' | 'info' | 'warn' | 'error'): void => {
    pill.style.borderColor = TONE_VAR[tone];
    pillDot.setAttribute('style', `display:inline-block;width:10px;height:10px;border-radius:50%;flex-shrink:0;background:${TONE_VAR[tone]};`);
    pillDot.setAttribute('aria-hidden', 'true');
  };

  function updateIndicators(): void {
    const eligibility = state.eligibility;
    if (!eligibility || eligibility.kind === 'not_applicable') {
      host!.hidden = true;
      if (modal) closePanel();
      return;
    }
    host!.hidden = false;
    pillBar.hidden = true;
    pillBar.removeAttribute('style');
    pillBar.replaceChildren();
    if (eligibility.kind === 'blocked') {
      live.textContent = 'Synchronisation indisponible';
      setPillTone('error');
      pill.title = eligibility.message;
      pill.setAttribute('aria-label', `Synchronisation indisponible. ${eligibility.message} Ouvrir les détails.`);
      attention.hidden = true;
      attention.style.display = 'none';
      return;
    }
    if (eligibility.kind === 'consent_required') {
      live.textContent = 'Synchronisation à activer';
      setPillTone('info');
      pill.title = eligibility.message;
      pill.setAttribute('aria-label', `Synchronisation à activer. ${eligibility.message} Ouvrir.`);
      attention.hidden = true;
      attention.style.display = 'none';
      return;
    }
    const label = state.label;
    const tone = label?.tone ?? 'info';
    const text = label ? pillText(label, state.status) : 'Synchronisation';
    live.textContent = text;
    setPillTone(tone);
    pill.title = label?.detail ?? '';
    pill.setAttribute('aria-label', `${text}. ${label?.detail ?? ''} Ouvrir les détails.`);
    if (state.status?.state === 'syncing') {
      pillBar.hidden = false;
      pillBar.append(buildProgressBar(state.status.progress, TEXT.syncing, true));
      pillBar.setAttribute('style', 'display:inline-flex;align-items:center;');
    }
    const count = Math.max(state.attentionCount, items.length);
    attention.hidden = count === 0;
    attention.style.display = count === 0 ? 'none' : '';
    if (count > 0) {
      attentionText$.textContent = attentionText(count);
      attention.title = 'Voir les modifications à examiner';
      attention.setAttribute('aria-label', `${attentionText(count)}. Ouvrir la liste.`);
    }
  }

  // --- données du panneau --------------------------------------------------------------------------------

  const loadAttention = async (): Promise<void> => {
    try {
      items = await runtime.listAttention();
      attentionLoad = 'ready';
    } catch {
      items = [];
      attentionLoad = 'error';
    }
  };

  const loadAdmin = async (): Promise<void> => {
    try {
      const input = await getInput();
      isAdmin = input.sessionRole === 'admin' || input.sessionRole === 'super_admin';
    } catch {
      isAdmin = false;
    }
  };

  const reload = async (): Promise<void> => {
    attentionLoad = 'loading';
    renderStatusRegions();
    await loadAttention();
    updateIndicators();
    renderStatusRegions();
  };

  // --- actions -------------------------------------------------------------------------------------------

  const reconnect = (): void => {
    if (opts.onReconnect) {
      opts.onReconnect();
      return;
    }
    void (async () => {
      const ok = await ask({
        title: TEXT.reconnectConfirmTitle,
        message: TEXT.reconnectConfirmMessage,
        confirmLabel: TEXT.reconnect,
        host: root,
      });
      if (ok) windowFunction('doLogout')?.();
    })();
  };

  const openUpdate = (): void => {
    if (opts.onUpdate) {
      opts.onUpdate();
      return;
    }
    const run = windowFunction('upgradeToLatestRelease') ?? windowFunction('installPendingAppUpdate');
    if (run) void Promise.resolve(run()).catch(() => ToastService.error('La mise à jour n’a pas pu démarrer. Réessayez dans un instant.'));
    else ToastService.info('La mise à jour sera proposée dès qu’elle est disponible.');
  };

  /** Fenêtre de confirmation existante ; le focus revient sur le bouton d'origine si on annule. */
  const ask = async (options: Parameters<typeof confirmAction>[0]): Promise<boolean> => {
    const origin = document.activeElement;
    const ok = await confirmAction(options);
    if (!ok && origin instanceof HTMLElement && origin.isConnected) origin.focus();
    return ok;
  };

  const confirmAbandon = (item: SyncAttentionItem): Promise<boolean> =>
    ask({
      title: 'Abandonner cette modification',
      message:
        item.kind === 'quarantine'
          ? `${itemTitle(item)}\nVotre copie mise de côté ne sera plus gardée. La version du serveur reste en place. Cette action est définitive.`
          : `${itemTitle(item)}\nVotre modification ne sera ni envoyée ni gardée. Cette action est définitive.`,
      confirmLabel: TEXT.abandon,
      danger: true,
      host: root,
    });

  const act = async (index: number, action: 'abandon' | 'retry' | 'copied'): Promise<void> => {
    const item = items[index];
    if (!item || busyId) return;
    if (action === 'abandon' && !(await confirmAbandon(item))) return;
    busyId = item.id;
    renderStatusRegions();
    try {
      const res = await runtime.resolveAttention(item, action);
      if (action === 'copied') ToastService.success(res.newId ? 'Nouvelle fiche créée à partir de la copie conservée.' : 'Copie traitée.');
      if (action === 'retry') ToastService.info('Modification remise en file d’envoi.');
      if (action === 'abandon') ToastService.info('Élément abandonné.');
    } catch {
      ToastService.error('Action impossible pour le moment. L’élément est conservé.');
    }
    busyId = null;
    await loadAttention();
    updateIndicators();
    renderStatusRegions();
    modal?.querySelector<HTMLElement>('[data-focus-key="attention-heading"]')?.focus();
  };

  const runSyncNow = async (): Promise<void> => {
    if (syncBusy) return;
    syncBusy = true;
    syncControls();
    try {
      await runtime.syncNow();
    } catch {
      ToastService.error('La synchronisation n’a pas pu être lancée. Vos données restent sur ce poste.');
    }
    syncBusy = false;
    await loadAttention();
    updateIndicators();
    syncControls();
    renderStatusRegions();
  };

  const exportDiagnostic = async (): Promise<void> => {
    try {
      const diag = await runtime.exportDiagnostic();
      downloadBlob(new Blob([JSON.stringify(diag, null, 2)], { type: 'application/json;charset=utf-8' }), `diagnostic-synchronisation-${Date.now()}.json`);
      ToastService.success('Diagnostic téléchargé (aucun contenu de vos données).');
    } catch {
      ToastService.error('Le diagnostic n’a pas pu être préparé pour le moment.');
    }
  };

  const withdraw = async (): Promise<void> => {
    const ok = await ask({
      title: TEXT.withdrawTitle,
      message: TEXT.withdrawMessage,
      confirmLabel: TEXT.withdrawTitle,
      danger: true,
      host: root,
    });
    if (!ok) return;
    try {
      const res = await runtime.withdrawConsent();
      ToastService.info(
        res.serverNotified
          ? 'Consentement retiré. La synchronisation est arrêtée.'
          : 'Consentement retiré sur ce poste. Le serveur sera prévenu au retour du réseau par le support.',
      );
      closePanel();
    } catch {
      ToastService.error('Le consentement n’a pas pu être retiré pour le moment. Rien n’a été modifié.');
    }
  };

  // --- panneau -------------------------------------------------------------------------------------------

  function syncControls(): void {
    if (!syncButton) return;
    const running = syncBusy || state.status?.state === 'syncing';
    syncButton.disabled = running;
    syncButton.textContent = running ? TEXT.syncing : TEXT.syncNow;
    syncButton.setAttribute('aria-busy', running ? 'true' : 'false');
  }

  function setRegion(key: string, region: HTMLElement, signature: string, build: () => Array<Node | null>): void {
    if (signatures.get(key) === signature) return;
    signatures.set(key, signature);
    replaceRegion(region, ...build());
  }

  function renderStatusRegions(): void {
    if (!regions || mode !== 'status') return;
    const { label, status } = state;
    const progress = status?.progress ? `${status.progress.done}/${status.progress.total}` : '-';
    setRegion(
      'state',
      regions.state,
      [label?.text, label?.detail, label?.tone, status?.state, status?.phase, progress, showsProgress(status)].join('|'),
      () => [buildStateBlock({ label, status, onReconnect: reconnect, onUpdate: openUpdate })],
    );
    const summary = getLastInitialSummary();
    setRegion('summary', regions.summary, summary ? JSON.stringify(summary) : '', () => [buildSummaryBlock(summary)]);
    setRegion(
      'counters',
      regions.counters,
      [status?.pendingCount, status?.lastSuccessAt, status?.lastPullAt, items.length, state.attentionCount].join('|'),
      () => [buildCountersBlock(status, attentionLoad === 'ready' ? items.length : state.attentionCount)],
    );
    setRegion(
      'attention',
      regions.attention,
      [attentionLoad, busyId, items.map((i) => `${i.id}:${i.code}`).join(',')].join('|'),
      () => [
        buildAttentionSection({
          load: attentionLoad,
          items,
          busyId,
          onAction: (index, action) => void act(index, action),
          onReload: () => void reload(),
        }),
      ],
    );
    const withdrawVisible = isAdmin && state.eligibility?.kind === 'eligible';
    const existing = regions.actions.querySelector('#hybrid-sync-withdraw');
    if (withdrawVisible && !existing) {
      const btn = h('button', { type: 'button', id: 'hybrid-sync-withdraw', class: 'btn btn-ghost btn-sm' }, TEXT.withdraw);
      btn.addEventListener('click', () => void withdraw());
      regions.actions.append(btn);
    } else if (!withdrawVisible && existing) {
      existing.remove();
    }
    syncControls();
  }

  const buildStatusSkeleton = (): HTMLElement => {
    const r = {
      state: h('div'),
      summary: h('div'),
      counters: h('div'),
      actions: h('div', { style: 'display:flex;gap:8px;flex-wrap:wrap;margin-bottom:18px;' }),
      attention: h('div'),
    };
    regions = r;
    signatures.clear();
    syncButton = h('button', { type: 'button', id: 'hybrid-sync-now', class: 'btn btn-primary btn-sm' }, TEXT.syncNow);
    syncButton.addEventListener('click', () => void runSyncNow());
    const diag = h('button', { type: 'button', id: 'hybrid-sync-diag', class: 'btn btn-secondary btn-sm' }, TEXT.diagnostic);
    diag.addEventListener('click', () => void exportDiagnostic());
    r.actions.append(syncButton, diag);
    return h('div', { id: 'hybrid-sync-content' }, r.state, r.summary, r.counters, r.actions, r.attention);
  };

  const buildConsent = (): HTMLElement => {
    const eligibility = state.eligibility;
    const canConsent = eligibility?.kind === 'consent_required' && eligibility.canConsent;
    const message = eligibility && 'message' in eligibility ? eligibility.message : TEXT.notAdmin;
    const built = buildConsentContent({
      canConsent,
      message,
      paragraphs: CONSENT_PARAGRAPHS,
      checkboxText: CONSENT_CHECKBOX_TEXT,
      onAccept: () => {
        const accept = built.accept;
        if (!accept) return;
        accept.disabled = true;
        const errorLine = built.content.querySelector<HTMLElement>('#hybrid-sync-consent-error');
        if (errorLine) errorLine.hidden = true;
        void (async () => {
          try {
            await runtime.acceptConsent(await getInput());
            ToastService.success(TEXT.consentOk);
            closePanel();
          } catch {
            accept.disabled = !built.check?.checked;
            if (errorLine) {
              errorLine.textContent = TEXT.consentFailed;
              errorLine.hidden = false;
            }
            ToastService.error(TEXT.consentFailed);
          }
        })();
      },
    });
    return built.content;
  };

  const wantedMode = (): PanelMode => (state.eligibility?.kind === 'consent_required' ? 'consent' : 'status');

  function renderPanel(): void {
    if (!modal) return;
    const wanted = wantedMode();
    if (mode !== wanted) {
      mode = wanted;
      regions = null;
      syncButton = null;
      modal.setAttribute('title', wanted === 'consent' ? TEXT.consentTitle : TEXT.panelTitle);
      const content = wanted === 'consent' ? buildConsent() : buildStatusSkeleton();
      const close = h('button', { type: 'button', id: 'hybrid-sync-close', class: 'btn btn-ghost' }, TEXT.close);
      close.addEventListener('click', () => closePanel());
      body?.replaceChildren(content, h('div', { style: 'display:flex;justify-content:flex-end;margin-top:18px;' }, close));
    }
    if (mode === 'status') renderStatusRegions();
  }

  function closePanel(): void {
    if (!modal || closing) return;
    closing = true;
    const current = modal;
    modal = null;
    body = null;
    mode = null;
    regions = null;
    syncButton = null;
    releaseTrap?.();
    releaseTrap = null;
    current.close();
    current.remove();
    closing = false;
    pill.setAttribute('aria-expanded', 'false');
    attention.setAttribute('aria-expanded', 'false');
  }

  const openPanel = async (): Promise<void> => {
    if (modal) return;
    const el = document.createElement('nexxus-modal') as ModalEl;
    el.id = PANEL_ID;
    el.setAttribute('size', 'lg');
    el.setAttribute('title', TEXT.panelTitle);
    el.addEventListener('close', () => {
      if (modal === el) closePanel();
    });
    // `position:relative` : sans lui, le contenu glissé dans la fenêtre n'a pas de parent de position et le piège de focus le croit invisible.
    body = h('div', { id: 'hybrid-sync-dialog-body', style: 'position:relative;' });
    el.appendChild(body);
    el.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePanel();
    });
    root.appendChild(el);
    modal = el;
    mode = null;
    attentionLoad = 'loading';
    pill.setAttribute('aria-expanded', 'true');
    attention.setAttribute('aria-expanded', 'true');
    el.open();
    renderPanel();
    releaseTrap = trapFocus(body, {
      onEscape: (event) => {
        event.preventDefault();
        event.stopPropagation();
        closePanel();
      },
    });
    await loadAdmin();
    if (modal !== el) return;
    if (wantedMode() === 'status') {
      await loadAttention();
      if (modal !== el) return;
      updateIndicators();
    }
    renderPanel();
  };

  // --- abonnements ---------------------------------------------------------------------------------------

  const unsubscribeSummary = subscribeInitialSummary(() => {
    if (modal && mode === 'status') renderStatusRegions();
  });

  const unsubscribe = runtime.subscribe((next) => {
    const before = state.attentionCount;
    state = next;
    if (next.tenantId !== lastTenant) {
      lastTenant = next.tenantId;
      clearInitialSummary();
    }
    updateIndicators();
    if (!modal) return;
    renderPanel();
    if (mode === 'status' && before !== next.attentionCount) {
      void loadAttention().then(() => {
        updateIndicators();
        renderStatusRegions();
      });
    }
  });

  const toggle = (): void => {
    if (modal) closePanel();
    else void openPanel();
  };
  pill.addEventListener('click', toggle);
  attention.addEventListener('click', toggle);

  // Réévaluation de l'éligibilité au montage (session ouverte, licence, société).
  void getInput()
    .then((input) => runtime.activate(input))
    .then(() => loadAttention())
    .then(() => updateIndicators())
    .catch(() => undefined);

  return () => {
    unsubscribe();
    unsubscribeSummary();
    closePanel();
    host.replaceChildren();
    host.hidden = true;
    root.querySelector(`#${PANEL_ID}`)?.remove();
  };
}
